import { useEffect, useRef, useState } from "react";
import { useIsFetching, useIsMutating } from "@tanstack/react-query";
import { getHttpPendingCount, HTTP_PENDING_COUNT_EVENT } from "@/services/httpService";

const getIsHtmlReady = () =>
    typeof document === "undefined" ? true : document.readyState === "complete";

/**
 * Tracks all sources of "busy" state (React Query fetches/mutations,
 * raw HTTP requests, and HTML readiness) and drives the top-bar
 * progress bar percentage.
 *
 * @returns {{ isBusy: boolean, progressPercent: number }}
 */
export function useLayoutLoader() {
    const fetchingCount = useIsFetching();
    const mutatingCount = useIsMutating();

    const [isHtmlReady, setIsHtmlReady] = useState(getIsHtmlReady);
    const [httpPendingCount, setHttpPendingCount] = useState(getHttpPendingCount);
    const [progressPercent, setProgressPercent] = useState(12);
    const loaderStatusRef = useRef({ api: null, htmlReady: null });

    // Track document readyState
    useEffect(() => {
        if (typeof window === "undefined" || typeof document === "undefined") return undefined;

        const update = () => setIsHtmlReady(document.readyState === "complete");
        update();
        window.addEventListener("load", update);
        document.addEventListener("readystatechange", update);
        return () => {
            window.removeEventListener("load", update);
            document.removeEventListener("readystatechange", update);
        };
    }, []);

    // Track raw HTTP pending requests
    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const handlePendingChange = (event) => {
            const nextCount = Number(event?.detail?.count);
            setHttpPendingCount(Number.isFinite(nextCount) ? nextCount : 0);
        };

        window.addEventListener(HTTP_PENDING_COUNT_EVENT, handlePendingChange);
        setHttpPendingCount(getHttpPendingCount());
        return () => window.removeEventListener(HTTP_PENDING_COUNT_EVENT, handlePendingChange);
    }, []);

    const isApiLoading = fetchingCount + mutatingCount + httpPendingCount > 0;
    const isBusy = isApiLoading || !isHtmlReady;

    // Dev logging
    useEffect(() => {
        const previous = loaderStatusRef.current;
        if (previous.api === isApiLoading && previous.htmlReady === isHtmlReady) return;

        if (location.hostname === "localhost") {
            console.info(
                `[HeaderLoader] API: ${isApiLoading ? "Loading" : "Ready"} | HTML: ${!isHtmlReady ? "Waiting" : "Ready"}`
            );
        }
        loaderStatusRef.current = { api: isApiLoading, htmlReady: isHtmlReady };
    }, [isApiLoading, isHtmlReady]);

    // Animate progress bar
    useEffect(() => {
        if (!isBusy) {
            setProgressPercent(100);
            return undefined;
        }

        setProgressPercent(12);
        const timer = setInterval(() => {
            setProgressPercent((prev) => {
                const next = prev + 7;
                return next >= 92 ? 18 : next;
            });
        }, 260);

        return () => clearInterval(timer);
    }, [isBusy]);

    return { isBusy, progressPercent };
}
